import React, { Component, useContext, } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { format, parse } from 'date-fns';
import { getJsDateFromExcel } from 'excel-date-to-js';
import Cookies from 'universal-cookie';
import { debugLogger } from '../Helpers/debugLogger';
import { fireOnboardingStep } from '../Helpers/helpApi';
import { clearSavedFilters } from '../Helpers/filterPersistence';

// Custom Components
import DataContext from './DataContext';
import { authContext, ProvideAuth, useAuth } from './AuthContext';
import { 
  createProject, 
  getProjectGeneralInformation, 
  getAllChangesByProjectId, 
  deleteProject, 
  createChange, 
  editChange, 
  deleteChange, 
  getProjectEntities, 
  getProjectDepartments, 
  getProjectAccountsMetadata,
  createMasterStat, 
  updateMasterStat, 
  deleteMasterStat, 
  getMasterStats, 
  getChangeAuditTrail, 
  getMasterStatAuditTrail,
  validateGLKeyUniqueness,
  validateAccountNameInTaxonomy,
  checkGLKeyDependencies,
  bulkDeleteGLKeys,
  bulkUpdateGLKeys,
  bulkUpdateMonthlyValues,
  createAccount,
  createEntity,
  updateEntity,
  deleteEntity,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  createParentAccount,
  getParentAccounts,
  updateParentAccount,
  deleteParentAccount,
  getParentAccountChildren,
  getParentAccountDepartments,
  getParentAccountDepartmentChildren,
  withTimeout,
} from '../Helpers/dataApi';
import {
  getSingleMonthChange,
  getStatDriverChanges
} from '../Helpers/aggrid';
import {
  getAccountDetails,
  getMonthsBetweenDates,
  isDate,
  removeDuplicates,
} from '../Helpers/utils';

// Validation pattern constants
const FORMULA_INJECTION_PATTERN = /^[\s]*[=+\-@\t\r]/;
const DANGEROUS_CHARS_PATTERN = /[;|\\<>]/; // Removed quotes and apostrophes - they're safe in account names
const NON_ASCII_PATTERN = /[^\x00-\x7F]/;


class DataProvider extends Component {
  static contextType = authContext;
  constructor(props) {
    super(props);

    // Guard flag for grid building to prevent concurrent builds
    this._isBuildingAgGridTable = false;
    
    // Mounted flag to prevent state updates after unmount
    this._isMounted = false;
    
    // Ref for audit trail cache - using ref to avoid triggering re-renders
    this.auditTrailCacheRef = {};
    
    // In-flight lazy-load promises keyed by `${projectId}:${parentAccount}`.
    // Stores the active Promise so concurrent expansions share the same request.
    this._inFlightParentLoads = new Map();

    this.state = {
      activeProject: false,
      activeProjectInfo: null, // null = not loaded yet, false = explicitly cleared
      activeProjectAttributes: {
        'accounts': [],
        'accountsMetadata': null,
        'data_retrieval_status': {
          'entities': {
            'retrieved': [],
            'notRetrieved': [],
          },
          'departments': {
            'retrieved': [],
            'notRetrieved': [],
          },
        },
        'departments': [],
        'drivers': [],
        'entities': [],
        'changes': [],
        'parent_changes': [],
        'calculatedAccounts': [],
        'dominantChanges': [],
        'masterStats': [],
      },
      navOpen: false,
      organizations: [],
      agGridData: [],
      // Lazy loading state for parent account children
      loadedParentChildren: new Map(), // `${projectId}:${parentAccount}` -> children array
      loadingParents: new Set(), // Track which parents are currently loading; keyed by `${projectId}:${parentAccount}`
    }
  }

  /**
   * Toggles the account menu dropdown
   */
  _toggleAccountMenu = () => {
    this.setState({
      accountMenuOpen: !this.state.accountMenuOpen
    });
  }

  /**
   * Toggles the navigation drawer
   */
  _toggleDrawer = () => {
    this.setState({
      navOpen: !this.state.navOpen
    })
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  removeNullValues = (data) => {
    data.forEach(d => {
      Object.keys(d).forEach(key => {
        if (d[key] === ' -   ') {
          d[key] = '0'
        }
      })
    })
  }

  /**
   * Derives the parent_changes array from a changes array.
   * parent_changes is never persisted — it is rebuilt at load time from the
   * parent_change field on each change so the grid can build column groups.
   *
   * @param {Array} changes - Array of change objects
   * @returns {Array} Array of { label, changes: [changeId, ...] } objects
   */
  _buildParentChanges = (changes) => {
    const map = new Map();
    (changes || []).forEach((change) => {
      const label = change.parent_change && change.parent_change.trim();
      if (label) {
        if (!map.has(label)) {
          map.set(label, { label, changes: [] });
        }
        map.get(label).changes.push(change.id);
      }
    });
    return Array.from(map.values());
  };

  _clearContextValues = () => {
    if (!this._isMounted) return;


    
    this.setState((prevState) => ({
      activeProject: false,
      activeProjectInfo: false, // Explicitly set to false when clearing
      activeProjectAttributes: {
        ...prevState.activeProjectAttributes,
        departments: [],
        accounts: [],
        accountsMetadata: null,
        drivers: [],
        entities: [],
        changes: [],
        parent_changes: [],
        calculatedAccounts: [],
      },
      agGridData: [],
      loadedParentChildren: new Map(), // Clears cached children for all parent accounts
      loadingParents: new Set(), // Clears in-flight lazy-load requests
    }));
    // Clear in-flight promises so stale requests from the previous project can't
    // populate child rows in the newly selected project.
    this._inFlightParentLoads.clear();
  }

  /**
   * Normalizes Acct Type values to match expected format
   * Handles common abbreviations like Rev, Exp, Ded, Oth Rev, Stat
   * @param {String} value - Raw value from upload
   * @returns {String} - Normalized value
   */
  normalizeAcctType = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '';
    
    const normalized = String(value).trim().toLowerCase();
    
    const acctTypeMap = {
      // Revenue variations
      'rev': 'Revenue',
      'revenue': 'Revenue',
      'revenues': 'Revenue',
      
      // Expense variations  
      'exp': 'Expense',
      'expense': 'Expense',
      'expenses': 'Expense',
      
      // Deduction variations
      'ded': 'Deduction',
      'deduction': 'Deduction',
      'deductions': 'Deduction',
      
      // Other Revenue variations
      'oth rev': 'Other Revenue',
      'other rev': 'Other Revenue',
      'other revenue': 'Other Revenue',
      
      // Asset variations
      'asset': 'Asset',
      'assets': 'Asset',
      
      // Liability variations
      'liability': 'Liability',
      'liabilities': 'Liability',
      
      // Equity variations
      'equity': 'Equity',
      
      // Stat variations
      'stat': 'Stat',
      'statistics': 'Stat',
      'statistical': 'Stat',
      
      // P&L variations
      'p&l': 'P&L',
      'p & l': 'P&L',
      'pnl': 'P&L',
      'p/l': 'P&L'
    };
    
    return acctTypeMap[normalized] || value;
  };

  /**
   * Normalizes Fixed or Variable values
   * Handles common abbreviations like F, V, Var, S, Stat
   * @param {String} value - Raw value from upload
   * @returns {String} - Normalized value
   */
  normalizeFixedOrVariable = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '';
    
    const normalized = String(value).trim().toLowerCase();
    
    const fixedVariableMap = {
      'f': 'Fixed',
      'fixed': 'Fixed',
      'v': 'Variable', 
      'var': 'Variable',
      'variable': 'Variable',
      's': 'Stat',
      'stat': 'Stat'
    };
    
    return fixedVariableMap[normalized] || value;
  };

  /**
   * Sanitizes project upload data by trimming whitespace, normalizing case, and cleaning values
   * @param {Array} projectData - Array of account rows from upload
   * @param {Object} projectDetails - Project info sheet data
   * @returns {Object} { projectData: [], projectDetails: {} } - Sanitized data
   */
  sanitizeProjectData = (projectData, projectDetails) => {
    // Sanitize project details
    const sanitizedProjectDetails = {
      'Project Title': String(projectDetails['Project Title'] || '').trim(),
      'Start Date': String(projectDetails['Start Date'] || '').trim(),
      'End Date': String(projectDetails['End Date'] || '').trim(),
    };
    
    // Sanitize each account row
    const sanitizedProjectData = projectData.map(data => {
      const acctType = this.normalizeAcctType(data['Acct Type']);
      
      return {
        // Use ?? (nullish coalescing) rather than || so that a numeric 0 is preserved
        // as '0' rather than coerced to '' by the falsy check. sanitizeProjectData runs
        // before validation, so '0' will be caught by isValidId if it's invalid — but
        // it won't be silently swallowed before validation runs.
        'Entity ID': String(data['Entity ID'] ?? '').trim(), // Coerce to string — API expects Str
        'Entity': String(data['Entity'] || '').trim(),
        'Department ID': String(data['Department ID'] ?? '').trim(), // Coerce to string — API expects Str
        'Department Name': String(data['Department Name'] || '').trim(),
        'Account ID': String(data['Account ID'] ?? '').trim(), // Coerce to string — API expects Str
        'Account Name': String(data['Account Name'] || '').trim().replace(/\s+/g, ' '), // Normalize whitespace
        'Parent Account': String(data['Parent Account'] || '').trim(),
        'Acct Type': acctType,
        'Fixed or Variable': this.normalizeFixedOrVariable(data['Fixed or Variable']),
        'Driver': acctType === 'Stat' ? '' : String(data['Driver'] || '').trim(), // Clear driver for Stat accounts
        // Preserve all date columns as-is (numeric values)
        ...Object.keys(data)
          .filter(key => !['Entity ID', 'Entity', 'Department ID', 'Department Name', 'Account ID', 'Account Name', 'Parent Account', 'Acct Type', 'Fixed or Variable', 'Driver'].includes(key))
          .reduce((acc, key) => ({ ...acc, [key]: data[key] }), {})
      };
    });
    
    return { projectData: sanitizedProjectData, projectDetails: sanitizedProjectDetails };
  };

  /**
   * Validates project upload data for:
   * - Required fields
   * - Data types
   * - Duplicate GL keys and account names
   * - Parent account existence and circular dependencies
   * - Driver account existence and type validation
   * - Hierarchical consistency
   * 
   * @param {Array} projectData - Array of account rows from upload
   * @param {Object} projectDetails - Project info sheet data
   * @returns {Object} { errors: [], warnings: [], isValid: boolean }
   */
  validateProjectData = (projectData, projectDetails) => {
    const errors = [];
    const warnings = [];
    
    // Helper to check if value is empty (null, undefined, empty string, or only whitespace)
    const isEmpty = (value) => {
      return value === null || value === undefined || String(value).trim() === '';
    };

    // Helper to check if value is a valid ID (alphanumeric with underscores)
    // Hyphens are intentionally excluded because GL keys use hyphen as a delimiter
    // (entityId-departmentId-accountId), so allowing hyphens in IDs would make
    // GL key parsing ambiguous.
    const isValidId = (value) => {
      if (isEmpty(value)) return false;
      const str = String(value).trim();
      // Accept alphanumeric strings (letters, numbers, underscores only)
      // Must start with a letter or number
      return /^[A-Za-z0-9][A-Za-z0-9_]*$/.test(str);
    };

    // Helper to check if value is a number (for date columns)
    const isNumeric = (value) => {
      if (isEmpty(value)) return false;
      return !isNaN(parseFloat(value)) && isFinite(value);
    };

    // 1. Validate that we have project details
    if (!projectDetails) {
      errors.push({
        type: 'dataset',
        field: 'Project Details',
        message: 'Project Info sheet is missing. Please ensure your template includes the Project Info sheet.',
        severity: 'error'
      });
      return { errors, warnings, isValid: false };
    }

    // Required project details fields — Project Title is validated via the form
    // field guard in newProject.jsx, not here

    if (isEmpty(projectDetails['Start Date'])) {
      errors.push({
        row: 'Project Info',
        field: 'Start Date',
        message: 'Start Date is required but missing',
        severity: 'error'
      });
    }

    if (isEmpty(projectDetails['End Date'])) {
      errors.push({
        row: 'Project Info',
        field: 'End Date',
        message: 'End Date is required but missing',
        severity: 'error'
      });
    }

    // Validate date range — dates may be in 'MMM yyyy' (e.g. "Jan 2023") or ISO format
    if (!isEmpty(projectDetails['Start Date']) && !isEmpty(projectDetails['End Date'])) {
      const REF = new Date(2000, 0, 1);
      const parseDate = (str) => {
        // Try MMM yyyy first (primary format from auto-detection)
        const d1 = parse(str, 'MMM yyyy', REF);
        if (!isNaN(d1.getTime())) return d1;
        // Fall back to native Date parsing (handles ISO, yyyy-MM-dd, etc.)
        const d2 = new Date(str);
        return d2;
      };
      const startDate = parseDate(projectDetails['Start Date']);
      const endDate = parseDate(projectDetails['End Date']);
      
      if (isNaN(startDate.getTime())) {
        errors.push({
          row: 'Project Info',
          field: 'Start Date',
          message: 'Start Date is not a valid date format',
          severity: 'error'
        });
      }
      
      if (isNaN(endDate.getTime())) {
        errors.push({
          row: 'Project Info',
          field: 'End Date',
          message: 'End Date is not a valid date format',
          severity: 'error'
        });
      }
      
      // Check if dates are valid and compare them
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && endDate <= startDate) {
        errors.push({
          row: 'Project Info',
          field: 'End Date',
          message: 'End Date must be after Start Date',
          severity: 'error'
        });
      }
    }

    // Check Project Title for formula injection
    if (FORMULA_INJECTION_PATTERN.test(projectDetails['Project Title'])) {
      errors.push({
        type: 'project',
        message: `Potential formula injection detected in Project Title. Value cannot start with =, +, -, @, or special characters.`,
        severity: 'error'
      });
    }

    // 2. Validate that we have data to process (Empty Dataset Validation)
    if (!projectData || projectData.length === 0) {
      errors.push({
        type: 'dataset',
        field: 'Project Data',
        message: 'Upload contains no account data. Please add at least one account row to your template.',
        severity: 'error'
      });
      return { errors, warnings, isValid: false };
    }

    // 3. Validate project data structure
    if (!Array.isArray(projectData)) {
      errors.push({
        type: 'dataset',
        message: 'Invalid project data format. Expected an array of account rows.',
        severity: 'error'
      });
      return { errors, warnings, isValid: false };
    }

    // Track GL keys for duplicate detection (GL key uniqueness is the only constraint)
    const glKeys = new Map(); // glKey -> [row numbers]

    // Get date columns from project details
    let dateColumns = [];
    if (!isEmpty(projectDetails['Start Date']) && !isEmpty(projectDetails['End Date'])) {
      try {
        dateColumns = getMonthsBetweenDates(projectDetails['Start Date'], projectDetails['End Date']);
      } catch (e) {
        // If we can't get date columns, we'll skip date column validation
      }
    }

    // Date column completeness validation
    if (dateColumns.length > 0 && projectData.length > 0) {
      // Get expected date columns from Project Info
      const expectedDates = dateColumns;

      // Get actual date columns from first row (exclude known field names)
      const knownFields = ['Entity ID', 'Entity', 'Department ID', 'Department Name', 'Account ID', 'Account Name', 'Parent Account', 'Acct Type', 'Fixed or Variable', 'Driver'];
      const actualColumns = Object.keys(projectData[0]);
      const actualDateColumns = actualColumns.filter(col => !knownFields.includes(col));

      // Helper function to normalize date strings for comparison
      // Accepts formats like "Jan 2024", "2024-01-01", "January 2024", "01/2024", etc.
      const normalizeDateColumn = (dateStr) => {
        try {
          // Try parsing as ISO date first (YYYY-MM-DD)
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const date = parse(dateStr, 'yyyy-MM-dd', new Date());
            return format(date, 'MMM yyyy');
          }
          
          // Try parsing as "Mon YYYY" or "Month YYYY" (also handles multiple spaces)
          if (/^[A-Za-z]{3,9}\s+\d{4}$/.test(dateStr)) {
            const trimmed = dateStr.trim().replace(/\s+/g, ' ');
            // Try "MMM yyyy" (e.g., "Jan 2024")
            let date = parse(trimmed, 'MMM yyyy', new Date());
            if (isNaN(date.getTime())) {
              // Fallback to "MMMM yyyy" (e.g., "January 2024")
              date = parse(trimmed, 'MMMM yyyy', new Date());
            }
            if (!isNaN(date.getTime())) {
              return format(date, 'MMM yyyy');
            }
          }
          
          // Try parsing as MM/YYYY or M/YYYY
          if (/^\d{1,2}\/\d{4}$/.test(dateStr)) {
            const [month, year] = dateStr.split('/');
            const date = new Date(year, parseInt(month) - 1, 1);
            return `${date.toLocaleString('en-US', { month: 'short' })} ${year}`;
          }
          
          // Return original if no format matches
          return dateStr;
        } catch (e) {
          return dateStr;
        }
      };

      // Normalize all expected and actual date columns
      const normalizedExpected = expectedDates.map(normalizeDateColumn);
      const normalizedActual = actualDateColumns.map(normalizeDateColumn);

      // Build a map from normalized column name -> actual column name in the file.
      // This allows later per-row validations to look up the correct actual key.
      // Last occurrence wins when multiple actual columns normalize to the same key.
      const seenNormalized = new Map(); // normalized key -> first actual column name
      const duplicateNormalizedKeys = new Set(); // actual column names involved in duplicates
      actualDateColumns.forEach((col, idx) => {
        const normalized = normalizedActual[idx];
        if (seenNormalized.has(normalized)) {
          // Track both the first occurrence and this duplicate
          duplicateNormalizedKeys.add(seenNormalized.get(normalized));
          duplicateNormalizedKeys.add(col);
        } else {
          seenNormalized.set(normalized, col);
        }
      });

      if (duplicateNormalizedKeys.size > 0) {
        warnings.push({
          type: 'dataset',
          message: `Duplicate date columns found (after normalization): ${[...duplicateNormalizedKeys].join(', ')}. Only the last occurrence will be used.`,
          severity: 'warning'
        });
      }

      const actualColumnMap = actualDateColumns.reduce((map, col, idx) => {
        map[normalizedActual[idx]] = col;
        return map;
      }, {});

      // Replace dateColumns with the actual column names present in the file so
      // downstream per-row numeric validation can find the right keys.
      dateColumns = expectedDates
        .map((date, idx) => actualColumnMap[normalizedExpected[idx]])
        .filter(Boolean);

      // Check for missing date columns (using normalized comparison)
      const missingDates = expectedDates.filter((date, idx) => {
        const normalized = normalizedExpected[idx];
        return !normalizedActual.includes(normalized);
      });
      
      if (missingDates.length > 0) {
        errors.push({
          type: 'dataset',
          message: `Missing expected date columns: ${missingDates.join(', ')}. Please ensure your template matches the selected date range.`,
          severity: 'error'
        });
      }

      // Check for extra unexpected columns (using normalized comparison)
      const extraColumns = actualDateColumns.filter((col, idx) => {
        const normalized = normalizedActual[idx];
        return !normalizedExpected.includes(normalized);
      });
      
      if (extraColumns.length > 0) {
        warnings.push({
          type: 'dataset',
          message: `Unexpected columns found: ${extraColumns.join(', ')}. These will be ignored.`,
          severity: 'warning'
        });
      }
    }

    // Valid Acct Type values
    const validAcctTypes = ['Revenue', 'Expense', 'Deduction', 'Other Revenue', 'Asset', 'Liability', 'Equity', 'Stat', 'P&L'];
    // These types are valid but won't appear in the main P&L sections — they land in "Other"
    const nonPLAcctTypes = ['Asset', 'Liability', 'Equity'];
    // These are the core P&L types needed for a meaningful income statement view
    const plAcctTypes = ['Revenue', 'Expense', 'Deduction', 'Other Revenue', 'P&L'];
    const validFixedOrVariable = ['Fixed', 'Variable', 'Stat', ''];

    projectData.forEach((row, index) => {
      const rowNumber = index + 2; // +2 to account for header row and 1-indexing

      // Required fields validation
      const requiredFields = [
        { field: 'Entity ID', key: 'Entity ID' },
        { field: 'Entity', key: 'Entity' },
        { field: 'Department ID', key: 'Department ID' },
        { field: 'Department Name', key: 'Department Name' },
        { field: 'Account ID', key: 'Account ID' },
        { field: 'Account Name', key: 'Account Name' },
        { field: 'Acct Type', key: 'Acct Type' }
      ];

      requiredFields.forEach(({ field, key }) => {
        if (isEmpty(row[key])) {
          errors.push({
            row: rowNumber,
            field: field,
            message: `${field} is required but missing`,
            severity: 'error'
          });
        }
      });

      // Data type validation - IDs must be alphanumeric
      if (!isEmpty(row['Entity ID']) && !isValidId(row['Entity ID'])) {
        errors.push({
          row: rowNumber,
          field: 'Entity ID',
          message: `Entity ID must be alphanumeric (letters, numbers, underscores only), got: "${row['Entity ID']}"`,
          severity: 'error'
        });
      }

      if (!isEmpty(row['Department ID']) && !isValidId(row['Department ID'])) {
        errors.push({
          row: rowNumber,
          field: 'Department ID',
          message: `Department ID must be alphanumeric (letters, numbers, underscores only), got: "${row['Department ID']}"`,
          severity: 'error'
        });
      }

      if (!isEmpty(row['Account ID']) && !isValidId(row['Account ID'])) {
        errors.push({
          row: rowNumber,
          field: 'Account ID',
          message: `Account ID must be alphanumeric (letters, numbers, underscores only), got: "${row['Account ID']}"`,
          severity: 'error'
        });
      }

      // GL Key duplicate detection - track all occurrences
      if (!isEmpty(row['Entity ID']) && !isEmpty(row['Department ID']) && !isEmpty(row['Account ID'])) {
        const glKey = `${row['Entity ID']}-${row['Department ID']}-${row['Account ID']}`;
        
        if (!glKeys.has(glKey)) {
          glKeys.set(glKey, [rowNumber]);
        } else {
          glKeys.get(glKey).push(rowNumber);
        }
      }

      // Acct Type validation (warning only for non-standard values)
      if (!isEmpty(row['Acct Type'])) {
        const acctType = String(row['Acct Type']).trim();
        if (!validAcctTypes.includes(acctType)) {
          warnings.push({
            row: rowNumber,
            field: 'Acct Type',
            message: `Acct Type "${acctType}" will be accepted but doesn't match standard types: ${validAcctTypes.join(', ')}`,
            severity: 'warning'
          });
        } else if (nonPLAcctTypes.includes(acctType)) {
          warnings.push({
            row: rowNumber,
            field: 'Acct Type',
            message: `Acct Type "${acctType}" is valid but will appear in the "Other" section of the project view, not in the main P&L (Net Patient Revenue, Other Revenue, or Operating Expenses).`,
            severity: 'info'
          });
        }
      }

      // Fixed or Variable validation (warning only for non-standard values)
      if (!isEmpty(row['Fixed or Variable'])) {
        const fixedOrVariable = String(row['Fixed or Variable']).trim();
        if (!validFixedOrVariable.includes(fixedOrVariable)) {
          warnings.push({
            row: rowNumber,
            field: 'Fixed or Variable',
            message: `Value "${fixedOrVariable}" will be accepted. Standard values are: Fixed, Variable, Stat, or blank`,
            severity: 'warning'
          });
        }
      }

      // Date column validation - all values must be numeric or empty
      dateColumns.forEach((dateCol) => {
        if (row.hasOwnProperty(dateCol) && !isEmpty(row[dateCol])) {
          if (!isNumeric(row[dateCol])) {
            errors.push({
              row: rowNumber,
              field: dateCol,
              message: `Value in date column "${dateCol}" must be numeric or empty, got: "${row[dateCol]}"`,
              severity: 'error'
            });
          }
        }
      });

      // Formula injection detection
      const stringFields = ['Entity', 'Department Name', 'Account Name', 'Parent Account', 'Driver'];
      
      stringFields.forEach(field => {
        const value = row[field];
        if (typeof value === 'string' && FORMULA_INJECTION_PATTERN.test(value)) {
          errors.push({
            row: rowNumber,
            field: field,
            message: `Potential formula injection detected in "${field}". Values cannot start with =, +, -, @, or special characters. Current value: "${value}"`,
            severity: 'error'
          });
        }
      });

      // Special character detection
      const accountName = String(row['Account Name'] || '');
      
      // Check for dangerous characters in Account Name
      if (DANGEROUS_CHARS_PATTERN.test(accountName)) {
        warnings.push({
          row: rowNumber,
          field: 'Account Name',
          message: `Account name "${accountName}" contains special characters (;|\\<>) that may cause issues in exports.`,
          severity: 'warning'
        });
      }
      
      // Check for non-ASCII characters (emoji, accented characters) in Account Name
      if (NON_ASCII_PATTERN.test(accountName)) {
        warnings.push({
          row: rowNumber,
          field: 'Account Name',
          message: `Account name "${accountName}" contains non-ASCII characters (emoji or special characters) that may cause encoding issues.`,
          severity: 'warning'
        });
      }
      
      // Check Department Name
      const deptName = String(row['Department Name'] || '');
      if (DANGEROUS_CHARS_PATTERN.test(deptName)) {
        warnings.push({
          row: rowNumber,
          field: 'Department Name',
          message: `Department name "${deptName}" contains special characters that may cause issues.`,
          severity: 'warning'
        });
      }
      
      // Check Entity
      const entityName = String(row['Entity'] || '');
      if (DANGEROUS_CHARS_PATTERN.test(entityName)) {
        warnings.push({
          row: rowNumber,
          field: 'Entity',
          message: `Entity name "${entityName}" contains special characters that may cause issues.`,
          severity: 'warning'
        });
      }

      // Numeric range validation
      const acctType = row['Acct Type'];
      dateColumns.forEach(date => {
        const value = row[date];
        
        if (value !== undefined && value !== null && value !== '') {
          const numValue = Number(value);
          
          if (!isNaN(numValue)) {
            // Flag extremely large values
            if (Math.abs(numValue) > 1000000000) {
              warnings.push({
                row: rowNumber,
                field: date,
                message: `Value for ${date} is extremely large (${numValue.toLocaleString()}). Please verify this is correct.`,
                severity: 'warning'
              });
            }
            
            // Warn about negative values in Revenue accounts
            if (acctType === 'Revenue' && numValue < 0) {
              warnings.push({
                row: rowNumber,
                field: date,
                message: `Revenue account has negative value for ${date} (${numValue.toLocaleString()}). This is unusual - please verify.`,
                severity: 'warning'
              });
            }
            
            // Warn about negative values in Asset accounts
            if (acctType === 'Asset' && numValue < 0) {
              warnings.push({
                row: rowNumber,
                field: date,
                message: `Asset account has negative value for ${date} (${numValue.toLocaleString()}). This is unusual - please verify.`,
                severity: 'warning'
              });
            }
          }
        }
      });
    });

    // ========== DATASET-LEVEL P&L STRUCTURE WARNINGS ==========

    const acctTypesPresent = new Set(projectData.map(r => String(r['Acct Type'] || '').trim()));
    const hasRevenue = acctTypesPresent.has('Revenue');
    const hasExpense = acctTypesPresent.has('Expense');
    const hasPLTypes = plAcctTypes.some(t => acctTypesPresent.has(t));
    const onlyNonPL = [...acctTypesPresent].every(t => nonPLAcctTypes.includes(t) || t === 'Stat' || t === '');

    if (!hasPLTypes) {
      warnings.push({
        type: 'dataset',
        field: 'Acct Type',
        message: `No P&L account types found (Revenue, Expense, Deduction, Other Revenue, P&L). The project view will show an empty income statement. Add at least Revenue and Expense accounts to see meaningful P&L data.`,
        severity: 'warning'
      });
    } else {
      if (!hasRevenue) {
        warnings.push({
          type: 'dataset',
          field: 'Acct Type',
          message: `No Revenue accounts found. The "Net Patient Revenue" section will be empty in the project view. Consider adding Revenue accounts for a complete income statement.`,
          severity: 'warning'
        });
      }
      if (!hasExpense) {
        warnings.push({
          type: 'dataset',
          field: 'Acct Type',
          message: `No Expense accounts found. The "Operating Expenses" section will be empty in the project view. Consider adding Expense accounts for a complete income statement.`,
          severity: 'warning'
        });
      }
    }

    if (onlyNonPL && acctTypesPresent.size > 0) {
      warnings.push({
        type: 'dataset',
        field: 'Acct Type',
        message: `All accounts are of type Asset, Liability, or Equity. These appear in the "Other" section only and will not contribute to the P&L income statement view.`,
        severity: 'warning'
      });
    }

    // ========== GENERATE DUPLICATE ERRORS WITH ALL OCCURRENCES ==========
    
    // Generate GL key duplicate errors with all occurrences
    glKeys.forEach((rowNumbers, glKey) => {
      if (rowNumbers.length > 1) {
        const [firstRow, ...duplicateRows] = rowNumbers;
        errors.push({
          row: firstRow,
          field: 'GL Key',
          message: `Duplicate GL key "${glKey}". First occurrence at row ${firstRow}, also found at row${duplicateRows.length > 1 ? 's' : ''}: ${duplicateRows.join(', ')}`,
          severity: 'error'
        });
      }
    });
    

    // ========== CROSS-ROW CONSISTENCY VALIDATIONS ==========
    
    // Build maps of ID -> { name, firstRow } for consistency checking
    const entityMappings = {}; // entityId -> { name, firstRow }
    const deptMappings = {}; // "entityId-deptId" -> { name, firstRow, entityName }
    const accountMappings = {}; // "entityId-deptId-acctId" -> { name, firstRow, deptName }

    projectData.forEach((data, index) => {
      const rowNum = index + 2;
      const entityId = String(data['Entity ID'] || '').trim();
      const entityName = String(data['Entity'] || '').trim();
      const deptId = String(data['Department ID'] || '').trim();
      const deptName = String(data['Department Name'] || '').trim();
      const acctId = String(data['Account ID'] || '').trim();
      const acctName = String(data['Account Name'] || '').trim();
      
      // Skip if entity ID is empty (will be caught by required field validation)
      if (!entityId) return;
      
      // Check Entity ID consistency
      if (entityMappings[entityId]) {
        // We've seen this Entity ID before - names must match exactly
        if (entityMappings[entityId].name !== entityName) {
          errors.push({
            row: rowNum,
            field: 'Entity',
            message: `Inconsistent Entity name for Entity ID ${entityId}. Row ${entityMappings[entityId].firstRow} has "${entityMappings[entityId].name}", but row ${rowNum} has "${entityName}". The same Entity ID must have the same name throughout the file.`,
            severity: 'error'
          });
        }
      } else {
        // First occurrence - store it as the "truth"
        entityMappings[entityId] = {
          name: entityName,
          firstRow: rowNum
        };
      }
      
      // Skip if department ID is empty (will be caught by required field validation)
      if (!deptId) return;
      
      // Check Department ID consistency (scoped by entity)
      const deptKey = `${entityId}-${deptId}`;
      if (deptMappings[deptKey]) {
        if (deptMappings[deptKey].name !== deptName) {
          errors.push({
            row: rowNum,
            field: 'Department Name',
            message: `Inconsistent Department name for Department ID ${deptId} in Entity "${entityName}". Row ${deptMappings[deptKey].firstRow} has "${deptMappings[deptKey].name}", but row ${rowNum} has "${deptName}". The same Department ID must have the same name within an entity.`,
            severity: 'error'
          });
        }
      } else {
        deptMappings[deptKey] = {
          name: deptName,
          firstRow: rowNum,
          entityName: entityName
        };
      }
      
      // Skip if account ID is empty (will be caught by required field validation)
      if (!acctId) return;
      
      // Check Account ID consistency (scoped by entity + department / GL Key)
      const glKey = `${entityId}-${deptId}-${acctId}`;
      if (accountMappings[glKey]) {
        if (accountMappings[glKey].name !== acctName) {
          errors.push({
            row: rowNum,
            field: 'Account Name',
            message: `Inconsistent Account name for Account ID ${acctId} in Department "${deptName}". Row ${accountMappings[glKey].firstRow} has "${accountMappings[glKey].name}", but row ${rowNum} has "${acctName}". The same Account ID must have the same name within a department.`,
            severity: 'error'
          });
        }
      } else {
        accountMappings[glKey] = {
          name: acctName,
          firstRow: rowNum,
          deptName: deptName
        };
      }
    });

    // ========== RELATIONSHIP VALIDATIONS ==========
    
    // Parent Account is a taxonomical label for reporting/display purposes.
    // It does not require validation as a referential integrity constraint.
    // - Parent categories (e.g., "Gross Rev", "Staff Salaries") do NOT need to exist as account rows
    // - Self-references (Account Name = Parent Account) are valid and indicate top-level accounts
    // - Parent categories can span across multiple departments
    
    // Build lookup maps for all accounts in the dataset by entity for O(1) access
    const accountLookupByEntity = {}; // For full account data lookup
    const normalizedAccountsByEntity = {}; // entityId -> Map(normalizedName -> originalName)
    
    projectData.forEach((data, index) => {
      const entityId = data['Entity ID'];
      const accountName = String(data['Account Name']).trim();
      const normalized = accountName.toLowerCase().replace(/\s+/g, ' '); // Collapse whitespace
      
      if (!accountLookupByEntity[entityId]) {
        accountLookupByEntity[entityId] = {};
        normalizedAccountsByEntity[entityId] = new Map();
      }
      accountLookupByEntity[entityId][accountName] = data;
      normalizedAccountsByEntity[entityId].set(normalized, accountName);
    });

    // Validate driver references
    projectData.forEach((data, index) => {
      const rowNum = index + 2;
      const driver = data['Driver'];
      const entityId = data['Entity ID'];
      const entityName = data['Entity'];
      const accountName = String(data['Account Name']).trim();
      const fixedOrVariable = data['Fixed or Variable'];
      const acctType = data['Acct Type'];
      
      // Only validate driver if it's explicitly set to a non-empty value
      const driverTrimmed = String(driver || '').trim();
      if (driverTrimmed !== '' && driverTrimmed.toLowerCase() !== 'n/a') {
        const driverNormalized = driverTrimmed.toLowerCase().replace(/\s+/g, ' ');
        const accountNormalized = accountName.toLowerCase().replace(/\s+/g, ' ');
        
        // Check if account references itself as driver (skip other validations if true)
        const isSelfReference = driverNormalized === accountNormalized;
        
        // Check if a Stat account has a Driver value (should be blank)
        // Skip this check for self-references as they're already handled
        if (!isSelfReference && acctType === 'Stat') {
          warnings.push({
            row: rowNum,
            field: 'Driver',
            message: `Stat accounts cannot have drivers. The Driver value "${driverTrimmed}" will be ignored and treated as blank.`,
            severity: 'warning'
          });
        }
        
        if (isSelfReference) {
          // Self-reference is valid - just inform the user (equivalent to leaving blank)
          warnings.push({
            row: rowNum,
            field: 'Driver',
            message: `${acctType === 'Stat' ? 'Stat account' : 'Account'} "${accountName}" references itself as a driver. This is allowed and treated the same as leaving the Driver field blank; the self-reference will be ignored.`,
            severity: 'info' // Informational only - strict mode
          });
        } else {
          // Check 1: Driver account exists in the same entity using case-insensitive lookup
          const actualDriverName = normalizedAccountsByEntity[entityId]?.get(driverNormalized);
          
          if (!actualDriverName) {
            errors.push({
              row: rowNum,
              field: 'Driver',
              message: `Driver "${driverTrimmed}" not found in Entity ${entityName} (ID: ${entityId}). Driver accounts must exist in the same entity.`,
              severity: 'error'
            });
          } else {
            // Get the driver account data
            const driverAccount = accountLookupByEntity[entityId]?.[actualDriverName];
            
            // Check 2: Driver account is a Stat account type (info-level - strict mode only)
            if (driverAccount && driverAccount['Acct Type'] !== 'Stat') {
              warnings.push({
                row: rowNum,
                field: 'Driver',
                message: `Driver "${driverTrimmed}" has Acct Type "${driverAccount['Acct Type']}". Drivers should typically be "Stat" accounts.`,
                severity: 'info' // Only shown in strict mode
              });
            }
          }
        }
      } else {
        // Only warn for Variable P&L accounts (Revenue, Expense, P&L) without drivers
        // Deductions, Assets, Liabilities, Other Revenue often don't need drivers
        const accountTypesThatNeedDrivers = ['Revenue', 'Expense', 'P&L'];

        if (fixedOrVariable === 'Variable' && accountTypesThatNeedDrivers.includes(acctType)) {
          warnings.push({
            row: rowNum,
            field: 'Driver',
            message: `Variable ${acctType} account "${data['Account Name']}" has no driver. Consider adding a stat driver for forecasting.`,
            severity: 'warning' // Standard warning
          });
        }
      }
    });

    // Separate warnings and infos in a single pass for efficiency
    const regularWarnings = [];
    const infoMessages = [];
    warnings.forEach(w => {
      if (w.severity === 'info') {
        infoMessages.push(w);
      } else {
        regularWarnings.push(w);
      }
    });

    return {
      errors,
      warnings: regularWarnings,
      infos: infoMessages,
      isValid: errors.length === 0
    };
  }

  /**
  * Accepts a new project file upload, validates the received data, and parses it into the formats used throughout the application.
  * @param {File} fileData Client upload file created by `newProject.js`
  * @param {Object} activeUser Active user info
  * @param {Number} organizationId Optional organization ID to use (defaults to activeUser.organization.id)
  * @returns {Promise<void>} Promise that resolves when the project is created and set as active
  * @throws {Error} If project creation fails or project cannot be set as active
  */
  _formatFileUploadData = async (fileData, activeUser, organizationId = null) => {

    let projectData = fileData[Object.keys(fileData)[0]];
    let projectDetails = fileData[Object.keys(fileData)[1]][0];

    const dates = getMonthsBetweenDates(projectDetails['Start Date'], projectDetails['End Date']);

    let newProjectData = projectData.map((data) => {
      let account = {
        // IDs are already coerced to strings by sanitizeProjectData — no cast needed here
        'account_id': data['Account ID'],
        'account_name': data['Account Name'],
        'acct_type': data['Acct Type'],
        'dates': {},
        'department_id': data['Department ID'],
        'department_name': data['Department Name'],
        'driver': data['Driver'],
        'entity': data['Entity'],
        'entity_id': data['Entity ID'],
        'fixed_or_variable': data['Fixed or Variable'],
        'parent_account': data['Parent Account'],
      }
      dates.forEach((date) => {
        account.dates[format(parse(date, 'MMM yyyy', new Date()), 'yyyy-MM-dd')] = Number(data[date]);
      });
      return account;
    });

    // Use provided organizationId if available, otherwise fall back to activeUser.organization.id
    const effectiveOrgId = organizationId !== null ? organizationId : activeUser.organization.id;

    // Debug logging to track organization ID being used


    let newProjectRequest = {
      'endDate': format(parse(projectDetails['End Date'], 'MMM yyyy', new Date()), 'yyyy-MM-dd'),
      'organizationId': effectiveOrgId,
      'accounts': newProjectData,
      'projectTitle': projectDetails['Project Title'],
      'startDate': format(parse(projectDetails['Start Date'], 'MMM yyyy', new Date()), 'yyyy-MM-dd'),
      'userId': activeUser.id,
    }

    // Return the promise chain to allow caller to await completion
    return createProject(newProjectRequest, activeUser.accessToken)
      .then((response) => {
        // console.log('createProject', response);
        if(response.status === 200){
          // Fire onboarding step for project creation
          fireOnboardingStep('create-project', activeUser.accessToken);
          // Also fire upload-template since this path always involves an Excel upload
          fireOnboardingStep('upload-template', activeUser.accessToken);
          // Return setActiveProject promise to chain properly
          return this._setActiveProject(response.data.id, activeUser.accessToken);
        }
        else{
          throw new Error(response.message || 'Failed to create project');
        }
      })
      .catch((err) => {
        console.error('Error in formatFileUploadData:', err);
        throw err; // Re-throw to allow caller to handle
      });
  }

  /**
   * Sets a new active project, queries the API for new active project data
   * @param {String} projectId ID of newly selected project
   * @param {String} accessToken Auth token
   */
  _setActiveProject = async (projectId, accessToken) => {
    let newActiveProjectInfo;
    let newActiveProjectAttributes = {
      'accounts': [],
      'departments': [],
      'drivers': [],
      'entities': [],
      'changes': [],
      'parent_changes': [],
      'calculatedAccounts': [],
    };

    try{
      // Wrap API call with timeout (30 seconds)
      const projectInfoResponse = await withTimeout(
        getProjectGeneralInformation(projectId, accessToken),
        30000,
        'Project info request'
      );

      if(projectInfoResponse.status === 200){
        newActiveProjectInfo = cloneDeep(projectInfoResponse.data);
        

      } else if(projectInfoResponse.status === 404) {
        throw new Error('Project not found or no longer accessible');
      } else if(projectInfoResponse.status === 403) {
        throw new Error('Access denied to this project');
      } else {
        // Ensure error message always includes a defined status and message
        const status = projectInfoResponse.status || 'No response status';
        const message = projectInfoResponse.message || projectInfoResponse.statusText || 'Unknown error';
        throw new Error(`Failed to load project: ${status} - ${message}`);
      }

      // Wrap changes API call with timeout (30 seconds)
      try {
        const changesResponse = await withTimeout(
          getAllChangesByProjectId(projectId, accessToken),
          30000,
          'Changes request'
        );

        if (changesResponse.status === 200) {
          const allChanges = cloneDeep(changesResponse.data);
          
          debugLogger.log('CONTEXT_DATA', 'Processing changes from API', {
            totalChanges: allChanges.length,
            projectId: projectId
          });
          
          const changes = [];
          const calculatedAccounts = [];
          const recursiveCalculatedAccounts = [];
          
          allChanges.forEach((change, index) => {
            const changeInfo = {
              id: change.id,
              label: change.change_label,
              function_operator: change.function_operator,
              operator_type: typeof change.function_operator,
              is_stat_driver: change.is_stat_driver,
              is_dominant: change.is_dominant_change,
              is_master_stat: change.is_master_stat_change,
              selected_accounts_count: change.selected_accounts?.length || 0,
              reference_accounts_count: change.reference_accounts?.length || 0
            };
            
            if (change.function_operator == 'calculatedAccount') {
              debugLogger.log('CALC_ACCOUNT', 'Found calculated account in API response', changeInfo);
              calculatedAccounts.push(change);
            }
            else if (change.function_operator == 'recursiveCalculatedAccount') {
              debugLogger.log('CALC_ACCOUNT', 'Found recursive calculated account in API response', changeInfo);
              recursiveCalculatedAccounts.push(change);
            }
            else {
              // Categorize regular changes for better debugging
              let category = 'CHANGE_REGULAR';
              if (change.is_master_stat_change) {
                category = 'MASTER_STAT';
              } else if (change.is_stat_driver) {
                category = 'CHANGE_STAT_DRIVER';
              } else if (change.is_dominant_change) {
                category = 'CHANGE_DOMINANT';
              } else if (change.function_operator === 'proForma') {
                category = 'PRO_FORMA';
              }
              
              debugLogger.log(category, 'Found change in API response', changeInfo);
              changes.push(change);
            }
          });
          
          debugLogger.log('CONTEXT_DATA', 'Changes categorized', {
            calculatedAccounts: calculatedAccounts.length,
            recursiveCalculatedAccounts: recursiveCalculatedAccounts.length,
            regularChanges: changes.length,
            calculatedAccountIds: calculatedAccounts.map(c => ({ id: c.id, label: c.change_label }))
          });
          
          newActiveProjectAttributes.changes = changes;
          newActiveProjectAttributes.calculatedAccounts = calculatedAccounts;
          newActiveProjectAttributes.parent_changes = this._buildParentChanges(changes);
        }
      } catch (changesErr) {
        // Log but don't fail the entire load if changes fail
        console.error('Error fetching change data (continuing with empty changes):', changesErr);
        newActiveProjectAttributes.changes = [];
        newActiveProjectAttributes.calculatedAccounts = [];
      }
      
      // Only update state if we successfully got project info
      if(newActiveProjectInfo) {
        // Set the activeProjectId cookie so navigation works correctly
        // This is especially important when creating a new project
        const cookies = new Cookies(null, { path: '/' });
        cookies.set('activeProjectId', projectId, { path: '/' });
        

        
        // Defer state update to avoid AG Grid timing issues,
        // but ensure this async method only resolves after the state is updated.
        await new Promise((resolve) => {
          setTimeout(() => {
            if (this._isMounted) {
              this.setState({
                activeProjectAttributes: newActiveProjectAttributes,
                activeProjectInfo: newActiveProjectInfo,
                // Reset lazy-loading caches when switching projects to prevent
                // stale children from a previous project being served.
                loadedParentChildren: new Map(),
                loadingParents: new Set(),
              });
            }
            resolve();
          }, 0);
        });
      } else {
        throw new Error('Project information could not be loaded');
      }
    }
    catch(err){
      console.error('Error fetching active project');
      console.error(err);
      throw err; // Re-throw to allow calling code to handle the error
    }
  }

  /**
   * Refreshes all project data for the current active project
   * Used when simulation changes to reload data with new permissions
   * @returns {Promise} Resolves when refresh is complete
   */
  _refreshActiveProject = async () => {
    if (!this._isMounted) return;
    
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    
    if (!activeProjectInfo || !activeProjectInfo.id || !activeUser || !activeUser.accessToken) {
      console.warn('Cannot refresh: no active project or user');
      return;
    }

    try {
      // Clear current data
      this._clearContextValues();
      
      // Reload project data with new simulation headers
      await this._setActiveProject(activeProjectInfo.id, activeUser.accessToken);
      

    } catch (err) {
      console.error('Error refreshing project data:', err);

      // If access to the active project was lost (e.g., due to simulation role/org change),
      // clear the active project selection/context and return without throwing, so that
      // callers are not left with a partially-updated simulation state or unhandled rejection.
      // _setActiveProject throws plain Error instances (no .response field), so we also
      // infer lost-access conditions from the error message.
      const status = err && err.response && err.response.status;
      const message = (err && err.message) || '';
      const lostAccess =
        status === 403 ||
        status === 404 ||
        /\b403\b/.test(message) ||
        /\b404\b/.test(message) ||
        /forbidden/i.test(message) ||
        /not\s+found/i.test(message);

      if (lostAccess) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            'Lost access to active project while refreshing; clearing active project context.'
          );
        }

        if (this._isMounted) {
          this.setState({
            activeProject: null,
            activeProjectInfo: false,
            activeProjectAttributes: {
              accounts: [],
              accountsMetadata: null,
              entities: [],
              departments: [],
              drivers: [],
              changes: [],
              parent_changes: [],
              calculatedAccounts: [],
              dominantChanges: [],
              masterStats: [],
              data_retrieval_status: {
                entities: { retrieved: [], notRetrieved: [] },
                departments: { retrieved: [], notRetrieved: [] },
              },
            },
          });
        }

        return;
      }

      // For unexpected errors, rethrow so they can be handled upstream.
      throw err;
    }
  }

  _setActiveProjectChangesAttribute = async (projectId, accessToken) => {
    try {
      const response = await getAllChangesByProjectId(projectId, accessToken);
      
      if (response.status === 200) {
        const changes = response.data;

        // Populate dominantChanges so calculateDominantChanges() works on initial
        // load, not just after a new change is saved in the current session.
        const dominantChanges = changes.filter(c => c.is_dominant_change);

        if (this._isMounted) {
          this.setState((prevState) => ({
            activeProjectAttributes: {
              ...prevState.activeProjectAttributes,
              changes,
              dominantChanges,
              parent_changes: this._buildParentChanges(changes),
            },
          }));
        }
  
        return changes;
      } else {
        console.warn(`Unexpected response status: ${response.status}`);
        return [];
      }
    } catch (err) {
      // console.log('Error trying to fetch project changes.');
      console.error(err);
      return [];
    }
  };

  /**
   * Sets drivers on the active project attributes
   * @param {Array} drivers Array of driver objects
   */
  _setActiveProjectDriversAttribute = (drivers) => {
    if (!this._isMounted) return;
    
    this.setState((prevState) => ({
      activeProjectAttributes: {
        ...prevState.activeProjectAttributes,
        drivers
      }
    }));
  }

  /**
   * Sets calculated accounts on the active project attributes
   * @param {Array} calculatedAccounts Array of calculated account objects
   */
  _setActiveProjectCalculatedAccountsAttribute = (calculatedAccounts) => {
    if (!this._isMounted) return;
    
    this.setState((prevState) => ({
      activeProjectAttributes: {
        ...prevState.activeProjectAttributes,
        calculatedAccounts
      }
    }));
  }

  /**
   * Delete a project
   * @param {Object} project Project to be deleted
   * @param {*} accessToken Auth token
   */
  _deleteProject = async (project, accessToken) => {
    let {
      activeProjectAttributes,
    } = this.state;
    
    const projectId = project.id;
    let success = {success: false, error: 'Deletion failed'};

    await deleteProject(projectId, accessToken)
      .then((response) => {
        // console.log('deleteProject', response);
        if(response.status === 200){
          success = {success: true};
          // Remove persisted filters for the deleted project so localStorage
          // doesn't accumulate orphaned entries indefinitely (Gap B fix).
          clearSavedFilters(projectId);
          if(activeProjectAttributes.id === projectId){
            this.setState({
              activeProject: false,
            });
          }
        }
        else{
          // console.log('delete failed');
          success = {success: false, error: response.message};
        }
      })
      .catch((err) => {
        console.error(err);
        success = {success: false, error: err};
      });
    return success;
  }

  /**
   * Saves newly created account change to the context and makes an API POST.
   * Adds new Parent Changes to activeProjectAttributes
   * @param {Object} newChange Newly created change
   * @param {Number} userId
   * @param {Number} projectId
   * @param {String} accessToken Auth token
   */
  _saveAccountChange = async (newChange, userId, projectId, accessToken) => {
    let {
      activeProjectAttributes,
    } = this.state;

    let newChangeData = cloneDeep(newChange);
    let changes = cloneDeep(activeProjectAttributes.changes);

    // Ensure date_range exists and is an array
    if (!newChangeData.date_range || !Array.isArray(newChangeData.date_range) || newChangeData.date_range.length === 0) {
      console.error('Invalid date_range in newChangeData:', newChangeData);
      throw new Error('date_range is required and must be a non-empty array');
    }

    const dateLength = newChangeData.date_range.length - 1;
    // Use parseISO (or parse with explicit format) to avoid the UTC-midnight timezone
    // shift that bare `new Date('YYYY-MM-DD')` causes in negative-UTC-offset timezones.
    // A bare ISO date string is treated as UTC midnight by the spec, so in e.g. UTC-6
    // "2024-01-01" becomes Dec 31 2023 locally and formats as "2023-12-31".
    // If the value is already in 'MMM yyyy' format (legacy path), parse it accordingly.
    const _parseDate = (s) => {
      if (!s) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        // ISO date: parse with explicit local-time constructor to avoid UTC shift
        const [y, m, d] = s.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      // Fallback: 'MMM yyyy' or other non-ISO string — let Date parse it (already local)
      return new Date(s);
    };
    const startDate = format(_parseDate(newChangeData.date_range[0]), 'yyyy-MM-dd');
    const endDate = format(_parseDate(newChangeData.date_range[dateLength]), 'yyyy-MM-dd');
    newChangeData.date_range = [startDate, endDate];
    


    await createChange(newChangeData, userId, projectId, accessToken)
      .then((response) => {
        // console.log('saveAccountChange response:', response);
        // console.log('saveAccountChange response.status:', response?.status);
        // console.log('saveAccountChange response.data:', response?.data);
        // Log error details if present
        if (response?.response) {
          // In production, log only essential error info
          console.error('saveAccountChange failed:', {
            status: response.response.status,
            changeLabel: newChangeData.change_label,
          });
          
          // In development, log full error details
          if (process.env.NODE_ENV !== 'production') {
            console.error('saveAccountChange error response:', response.response);
            console.error('saveAccountChange error data:', response.response.data);
            console.error('saveAccountChange error details:', JSON.stringify(response.response.data, null, 2));
          }
        }
        if(response.status === 200 || response.status === 201){
          newChangeData.id = response.data.id;
          
          // Fire onboarding step for first bridge column
          fireOnboardingStep('add-bridge-column', accessToken);

          // Check if this is a calculated account
          const isCalculatedAccount = newChangeData.function_operator === 'calculatedAccount' || 
                                      newChangeData.function_operator === 'recursiveCalculatedAccount';
          
          debugLogger.log(
            isCalculatedAccount ? 'CALC_ACCOUNT' : 'CHANGE_CREATE',
            'Change saved successfully',
            {
              changeId: newChangeData.id,
              changeLabel: newChangeData.change_label,
              functionOperator: newChangeData.function_operator,
              isCalculatedAccount,
              selectedAccountsCount: newChangeData.selected_accounts?.length || 0,
              referenceAccountsCount: newChangeData.reference_accounts?.length || 0,
              isDominant: newChangeData.is_dominant_change,
              isStatDriver: newChangeData.is_stat_driver,
              isMasterStat: newChangeData.is_master_stat_change
            }
          );
          
          // Add to appropriate array based on function_operator
          if (isCalculatedAccount) {
            // Add to calculatedAccounts array - create new array reference for React change detection
            const updatedCalculatedAccounts = [
              ...(activeProjectAttributes.calculatedAccounts || []),
              newChangeData
            ];
            
            debugLogger.log('CALC_ACCOUNT', 'Added to calculatedAccounts array', {
              changeId: newChangeData.id,
              changeLabel: newChangeData.change_label,
              newCount: updatedCalculatedAccounts.length,
              allCalculatedAccounts: updatedCalculatedAccounts.map(c => ({
                id: c.id,
                label: c.change_label
              }))
            });
            
            // Update the local reference for setState
            activeProjectAttributes.calculatedAccounts = updatedCalculatedAccounts;
          } else {
            // Add to regular changes array
            changes.push(newChangeData);
            
            debugLogger.log('CHANGE_CREATE', 'Added to changes array', {
              changeId: newChangeData.id,
              changeLabel: newChangeData.change_label,
              newCount: changes.length
            });
          }

          // Build parent change. If parent change doesn't exist, make a new one. If it does, add the new change to the existing parent change.
          const parent_change = newChangeData.parent_change;
          const parent_changes = activeProjectAttributes.parent_changes;
          const parentChangeIndex = parent_changes.findIndex(pc => pc.label === parent_change);

          if (parentChangeIndex > -1) {
            parent_changes[parentChangeIndex].changes.push(newChangeData.id);
          } else {
            parent_changes.push({
              label: (parent_change.length > 0 ? parent_change : newChangeData.change_label),
              changes: [newChangeData.id],
            });
          }
          activeProjectAttributes.parent_changes = parent_changes;

          if(newChange.is_dominant_change){
            if (!activeProjectAttributes.dominantChanges) {
              activeProjectAttributes.dominantChanges = [];
            }
            activeProjectAttributes.dominantChanges.push(newChangeData);
            
            debugLogger.log('CHANGE_DOMINANT', 'Added to dominant changes', {
              changeId: newChangeData.id,
              changeLabel: newChangeData.change_label
            });
          }

          this.setState((prevState) => ({
            activeProjectAttributes: {
              ...prevState.activeProjectAttributes,
              changes,
              calculatedAccounts: activeProjectAttributes.calculatedAccounts
            },
          }));
        } else {
          console.error('saveAccountChange failed with status:', response.status);
          newChangeData = false;
        }
      })
      .catch((err) => {
        console.error(err);
        newChangeData = false;
      });
      return newChangeData;
  }

  /**
   * Save edits to existing account change to the context and makes an API PUT.
   * @param {Object} change Newly created change
   * @param {Number} userId
   * @param {Number} projectId
   * @param {String} accessToken Auth token
   */
  _editAccountChange = async (change, userId, projectId, accessToken) => {
    let {
      activeProjectAttributes,
    } = this.state;

    let success = false;
    // console.log('edit change:', change);
    await editChange(change, userId, projectId, accessToken)
      .then((response) => {
        // console.log('editAccountChange',  response);
        if(response.status === 200){
          let updatedActiveProject = activeProjectAttributes;
          const changeIndex = updatedActiveProject.changes.findIndex(obj => obj.id === change.id);
          if(changeIndex > -1){
            updatedActiveProject.changes[changeIndex] = change;
          }

          let updatedActiveProjectAttributes = activeProjectAttributes
          const parent_change = change.parent_change;
          const parent_changes = updatedActiveProjectAttributes.parent_changes;
          const parentChangeIndex = parent_changes.findIndex(pc => pc.label === parent_change);

          if (parentChangeIndex > -1) {
            parent_changes[parentChangeIndex].changes.push(change.id);
          } else {
            parent_changes.push({
              label: (parent_change.length > 0 ? parent_change : change.change_label),
              changes: [change.id],
            });
          }

          updatedActiveProjectAttributes.parent_changes = parent_changes;

          this.setState({
            activeProject: updatedActiveProject,
            activeProjectAttributes: updatedActiveProjectAttributes,
          });
          success = true;
        }
        else{
          success = false;
        }
      })
      .catch((err) => {
        console.error(err);
      });
      return success;
  }

  _deleteAccountChange = async (changeId, parent_change, accessToken) => {
    let {
      activeProject,
      activeProjectAttributes,
    } = this.state;

    let success = false;

    await deleteChange(changeId, accessToken)
      .then((response) => {
        // console.log('deleteChange', response);

        if(response.status === 200){
          // Update changes in activeProjectAttributes (not activeProject)
          const updatedChanges = (activeProjectAttributes.changes || []).filter((change) => { return change.id !== changeId });
          
          let updatedActiveProjectAttributes = { ...activeProjectAttributes };
          updatedActiveProjectAttributes.changes = updatedChanges;
          
          // Update parent_changes
          const parentChangeIndex = activeProjectAttributes.parent_changes.findIndex(pc => pc.label === parent_change);
          if(parentChangeIndex > -1){
            const updatedEntry = {
              ...activeProjectAttributes.parent_changes[parentChangeIndex],
              changes: (activeProjectAttributes.parent_changes[parentChangeIndex].changes || []).filter((cId) => cId !== changeId)
            };
            updatedActiveProjectAttributes.parent_changes = [...activeProjectAttributes.parent_changes];
            if (updatedEntry.changes.length >= 2) {
              // Still a valid group — keep the entry
              updatedActiveProjectAttributes.parent_changes[parentChangeIndex] = updatedEntry;
            } else {
              // 0 or 1 change remaining — remove the parent entry entirely so the
              // surviving change is treated as standalone on any future column rebuild
              updatedActiveProjectAttributes.parent_changes.splice(parentChangeIndex, 1);
            }
          }
          
          // Update calculatedAccounts array if this was a calculated account
          if (activeProjectAttributes.calculatedAccounts) {
            const wasCalculatedAccount = activeProjectAttributes.calculatedAccounts.some(ca => ca.id === changeId);
            if (wasCalculatedAccount) {
              updatedActiveProjectAttributes.calculatedAccounts = activeProjectAttributes.calculatedAccounts.filter(
                ca => ca.id !== changeId
              );
            }
          }

          this.setState({
            activeProjectAttributes: updatedActiveProjectAttributes,
          });
          success = true;
        }
        else{
          success = false;
        }
      })
      .catch((err) => {
        console.error('Error deleting change:', err);
        success = false;
      });
      return success;
  }

  _buildAgGridTable = () => {
    // Guard to prevent concurrent builds
    if (this._isBuildingAgGridTable) {
      console.warn('AG Grid table build already in progress, skipping duplicate call');
      // Return null to explicitly signal that a build is already in progress
      // Callers should check agGridData state for the final result
      return null;
    }
    
    // Guard to prevent building after unmount
    if (!this._isMounted) {
      console.warn('Component unmounted, skipping AG Grid table build');
      return null;
    }
    
    this._isBuildingAgGridTable = true;
    
    const agGridData = [];
    
    let {
      activeProjectAttributes,
    } = this.state;

    if(activeProjectAttributes.accountsMetadata && activeProjectAttributes.accountsMetadata.length > 0) {
   
      activeProjectAttributes.accountsMetadata.forEach((account) => {
        const accountRow = {
          accountName: account.account_name,
          accountId: account.account_id,
          acctType: account.acct_type,
          basePeriodDates: account.dates,
          changes: [],
          department: account.department_name,
          departmentId: account.department_id,
          driver: account.driver ?? '',
          entity: account.entity,
          entityId: account.entity_id,
          fixedOrVariable: account.fixed_or_variable,
          glKey: `${account.entity_id}-${account.department_id}-${account.account_id}`,
          parentAccount: account.parent_account,
          ...account.dates,
        }
        agGridData.push(accountRow);
      })
    }

    // Defer setState to avoid timing conflicts with AG Grid render cycle
    setTimeout(() => {
      try {
        if (this._isMounted) {
          this.setState((prevState) => ({
            agGridData: agGridData
          }));
        }
      } finally {
        this._isBuildingAgGridTable = false;
      }
    }, 0);

    return agGridData
  }

  /**
  * Sets entities on the active project attributes, queries the API for project entities data
  * @param {Number} projectId Id of selected project
  * @param {String} accessToken Auth token
  */
   _setActiveProjectEntitiesAttribute = async (projectId, accessToken) => {
    try {
      // Wrap with timeout (20 seconds)
      const response = await withTimeout(
        getProjectEntities(projectId, accessToken),
        20000,
        'Entities request'
      );

      if(response.status === 200){
        const activeProjectEntities = response.data;
        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            entities: activeProjectEntities,
          },
        }));
        return activeProjectEntities;
      }
      return [];
    } catch (err) {
      console.error("Error trying to fetch project entities.");
      console.error(err);
      return [];
    }
  }

  /**
  * Sets departments on the active project attributes, queries the API for project departments data
  * @param {Number} projectId Id of selected project
  * @param {String} entitiesList A list with the Ids of the selected entities
  * @param {String} accessToken Auth token
  */
  _setActiveProjectDepartmentsAttribute = async (projectId, entitiesList, accessToken) => {
    const entities_ids_list = entitiesList.map((entity) => entity.entity_id);
    const entities_ids_list_to_string = entities_ids_list.toString();
  
    try {
      // Wrap with timeout (20 seconds)
      const departmentsPromise = getProjectDepartments(projectId, entities_ids_list_to_string, accessToken);
      const response = await Promise.race([
        departmentsPromise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Departments request timed out after 20 seconds')), 20000)
        )
      ]);

      if (response.status === 200) {
        const activeProjectDepartments = response.data; 
        const departments = [];

        activeProjectDepartments.forEach((entity) => {
          const { entity_id, entity_name, departments_list } = entity;

          departments_list.forEach((department) => {
            const { department_id, department_name } = department;
            departments.push({
              entityId: entity_id,  
              entityName: entity_name,
              departmentId: department_id,
              departmentName: department_name,
            });
          });
        });

        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            departments,
          },
        }));
        
        return departments;
      }
      return [];
    } catch (err) {
      console.error('Error trying to fetch project departments.');
      console.error(err);
      return [];
    }
  };

  /**
  * Sets accounts on the active project attributes, queries the metadata API for project accounts
  * @param {Number} projectId Id of selected project
  * @param {String} accessToken Auth token
  */
  _setActiveProjectAccountsAttribute = async (projectId, accessToken) => {
    try {
      const response = await withTimeout(
        getProjectAccountsMetadata(projectId, accessToken),
        30000,
        'Accounts request'
      );

      if (response.status === 200) {
        const accountsMetadata = response.data.accounts;

        // Deduplicate by account_id — the accounts dropdown only needs unique account
        // types. Department-specific filtering is handled by glKeyCache in the change form.
        //
        // NOTE: This dedup keeps the FIRST occurrence of each account_id, so the stored
        // departmentId/entityId will be from whichever department appears first in the
        // response. This is intentional — formattedAccounts is used exclusively for the
        // account name/label dropdown (account selector UI) and is never used to look up
        // department or entity context. All department-specific lookups use
        // accountsMetadata (the undeduped array stored alongside formattedAccounts).
        const seenAccountIds = new Set();
        const formattedAccounts = [];
        accountsMetadata.forEach((account) => {
          if (!seenAccountIds.has(account.account_id)) {
            seenAccountIds.add(account.account_id);
            formattedAccounts.push({
              departmentId: account.department_id,
              entityId: account.entity_id,
              id: account.account_id,
              label: account.account_name,
              parentAccount: account.parent_account,
            });
          }
        });

        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            accounts: formattedAccounts,
            accountsMetadata,
          },
        }));
      }
    } catch (err) {
      console.error('Error trying to fetch project accounts.');
      console.error(err);
    }
  }

  /**
   * Create a new Master Stat
   * @param {Object} masterStatData - Master Stat configuration
   * @param {String} userId - User ID
   * @param {String} projectId - Project ID
   * @param {String} accessToken - Auth token
   * @returns {Boolean} Success status
   */
  _createMasterStat = async (masterStatData, userId, projectId, accessToken) => {
    let success = false;
    
    await createMasterStat(masterStatData, userId, projectId, accessToken)
      .then((response) => {
        // console.log('createMasterStat', response);
        
        if (response.status === 200 || response.status === 201) {
          const newMasterStat = response.data;
          
          this.setState((prevState) => ({
            activeProjectAttributes: {
              ...prevState.activeProjectAttributes,
              masterStats: [...prevState.activeProjectAttributes.masterStats, newMasterStat],
            },
          }));
          success = true;
        }
      })
      .catch((err) => {
        console.error('Error creating Master Stat:', err);
        success = false;
      });
    
    return success;
  }

  /**
   * Update an existing Master Stat
   * @param {String} masterStatId - Master Stat ID
   * @param {Object} masterStatData - Updated Master Stat configuration
   * @param {String} userId - User ID
   * @param {String} projectId - Project ID
   * @param {String} accessToken - Auth token
   * @returns {Boolean} Success status
   */
  _updateMasterStat = async (masterStatId, masterStatData, userId, projectId, accessToken) => {
    let success = false;
    
    await updateMasterStat(masterStatId, masterStatData, userId, projectId, accessToken)
      .then((response) => {
        // console.log('updateMasterStat', response);
        
        if (response.status === 200) {
          const updatedMasterStat = response.data;
          
          this.setState((prevState) => ({
            activeProjectAttributes: {
              ...prevState.activeProjectAttributes,
              masterStats: prevState.activeProjectAttributes.masterStats.map(ms =>
                ms.id === masterStatId ? updatedMasterStat : ms
              ),
            },
          }));
          success = true;
        }
      })
      .catch((err) => {
        console.error('Error updating Master Stat:', err);
        success = false;
      });
    
    return success;
  }

  /**
   * Delete a Master Stat
   * @param {String} masterStatId - Master Stat ID
   * @param {String} projectId - Project ID
   * @param {String} accessToken - Auth token
   * @returns {Boolean} Success status
   */
  _deleteMasterStat = async (masterStatId, projectId, accessToken) => {
    let success = false;
    
    await deleteMasterStat(masterStatId, projectId, accessToken)
      .then((response) => {
        // console.log('deleteMasterStat', response);
        
        if (response.status === 200 || response.status === 204) {
          this.setState((prevState) => ({
            activeProjectAttributes: {
              ...prevState.activeProjectAttributes,
              masterStats: prevState.activeProjectAttributes.masterStats.filter(
                ms => ms.id !== masterStatId
              ),
            },
          }));
          success = true;
        }
      })
      .catch((err) => {
        console.error('Error deleting Master Stat:', err);
        success = false;
      });
    
    return success;
  }

  /**
   * Get all Master Stats for a project
   * @param {String} projectId - Project ID
   * @param {String} accessToken - Auth token
   */
  _getMasterStats = async (projectId, accessToken) => {
    try {
      const response = await getMasterStats(projectId, accessToken);
      
      if (response.status === 200) {
        const masterStats = response.data || [];
        
        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            masterStats: masterStats,
          },
        }));
        
        return masterStats; // Return the data for use in calling code
      }
      return [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  /**
   * Fetch and cache audit trail for a change
   * Uses a ref to avoid triggering re-renders when caching
   * @param {Number} changeId - Change ID
   * @param {String} accessToken - Auth token
   * @returns {Promise<Array>} Audit trail array
   */
  fetchAndCacheAuditTrail = async (changeId, accessToken) => {
    // Use prefixed cache key for consistency with master stats
    const cacheKey = `change_${changeId}`;
    
    // Check if already cached in ref
    if (this.auditTrailCacheRef[cacheKey]) {
      return this.auditTrailCacheRef[cacheKey];
    }

    const response = await getChangeAuditTrail(changeId, accessToken);
    
    if (response && response.status === 200) {
      const auditTrail = response.data || [];
      
      // Cache in ref (doesn't trigger re-render)
      this.auditTrailCacheRef[cacheKey] = auditTrail;
      
      return auditTrail;
    }

    if (response && response.status !== 200) {
      console.error('Failed to fetch change audit trail. Status:', response.status);
    }
    
    // Handle error responses (getChangeAuditTrail returns error object instead of throwing)
    console.error('Error fetching audit trail:', response);
    return [];
  }

  /**
   * Fetch and cache audit trail for a master stat
   * Uses a ref to avoid triggering re-renders when caching
   * @param {Number} masterStatId - Master Stat ID
   * @param {Number} projectId - Project ID
   * @param {String} accessToken - Auth token
   * @returns {Promise<Array>} Audit trail array
   */
  fetchAndCacheMasterStatAuditTrail = async (masterStatId, projectId, accessToken) => {
    const cacheKey = `masterStat_${masterStatId}`;
    
    // Check if already cached in ref
    if (this.auditTrailCacheRef[cacheKey]) {
      return this.auditTrailCacheRef[cacheKey];
    }

    if (!projectId) {
      console.error('No project ID provided');
      return [];
    }
    
    const response = await getMasterStatAuditTrail(projectId, masterStatId, accessToken);
    
    if (response && response.status === 200) {
      const auditTrail = response.data || [];
      
      // Cache in ref (doesn't trigger re-render)
      this.auditTrailCacheRef[cacheKey] = auditTrail;
      
      return auditTrail;
    }

    if (response && response.status !== 200) {
      console.error('Failed to fetch master stat audit trail. Status:', response.status);
    }
    
    // Handle error responses (getMasterStatAuditTrail returns error object instead of throwing)
    console.error('Error fetching master stat audit trail:', response);
    return [];
  }

  /**
   * Validate GL Key uniqueness
   * @param {String} entityId - Entity ID
   * @param {String} departmentId - Department ID
   * @param {String} accountId - Account ID
   * @param {Number} excludeId - Optional ID to exclude from check (for edits)
   * @returns {Promise<boolean>} True if GL Key exists, false otherwise
   */
  _validateGLKeyUniqueness = async (entityId, departmentId, accountId, excludeId = null) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return false;
    }

    try {
      const response = await validateGLKeyUniqueness(
        accessToken,
        activeProjectInfo.id,
        entityId,
        departmentId,
        accountId,
        excludeId
      );
      
      if (response.status === 200) {
        return response.data.exists;
      }
      return false;
    } catch (error) {
      console.error('Error validating GL Key uniqueness:', error);
      return false;
    }
  };

  /**
   * Validate account name uniqueness within taxonomy
   * @param {String} entityId - Entity ID
   * @param {String} departmentId - Department ID
   * @param {String} parentAccount - Parent account identifier
   * @param {String} accountName - Account name to validate
   * @param {Number} excludeId - Optional ID to exclude from check (for edits)
   * @returns {Promise<boolean>} True if account name exists in taxonomy, false otherwise
   */
  _validateAccountNameInTaxonomy = async (entityId, departmentId, parentAccount, accountName, excludeId = null) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return false;
    }

    try {
      const response = await validateAccountNameInTaxonomy(
        accessToken,
        activeProjectInfo.id,
        entityId,
        departmentId,
        parentAccount,
        accountName,
        excludeId
      );
      
      if (response.status === 200) {
        return response.data.exists;
      }
      return false;
    } catch (error) {
      console.error('Error validating account name in taxonomy:', error);
      return false;
    }
  };

  /**
   * Check GL Key dependencies before deletion
   * @param {String} glKey - GL Key to check (format: EntityID-DepartmentID-AccountID)
   * @returns {Promise<Object|null>} Dependency information or null on error
   */
  _checkGLKeyDependencies = async (glKey) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await checkGLKeyDependencies(
        accessToken,
        activeProjectInfo.id,
        glKey
      );
      
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error checking GL Key dependencies:', error);
      return null;
    }
  };

  /**
   * Bulk delete GL Keys
   * @param {Array} accountIds - Array of account IDs to delete
   * @returns {Promise<Object|null>} Result with deleted and failed arrays, or null on error
   */
  _bulkDeleteGLKeys = async (accountIds) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await bulkDeleteGLKeys(
        accessToken,
        activeProjectInfo.id,
        accountIds
      );
      
      if (response.status === 200) {
        const result = response.data;
        
        // Remove successfully deleted accounts from state
        if (result.deleted && result.deleted.length > 0) {
          this.setState((prevState) => {
            const prevAttributes = prevState.activeProjectAttributes || {};

            const updatedAccounts = (prevAttributes.accounts || []).filter(
              account => !result.deleted.includes(account.id)
            );

            const updatedAccountsMetadata = (prevAttributes.accountsMetadata || []).filter(
              account => !result.deleted.includes(account.id)
            );

            return {
              activeProjectAttributes: {
                ...prevAttributes,
                accounts: updatedAccounts,
                accountsMetadata: updatedAccountsMetadata,
              },
            };
          });
        }
        
        return result;
      }
      return null;
    } catch (error) {
      console.error('Error bulk deleting GL Keys:', error);
      return null;
    }
  };

  /**
   * Bulk update GL Keys
   * @param {Array} updates - Array of account update objects
   * @returns {Promise<Object|null>} Result with successful count and failed array, or null on error
   */
  _bulkUpdateGLKeys = async (updates) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await bulkUpdateGLKeys(
        accessToken,
        activeProjectInfo.id,
        updates
      );
      
      if (response.status === 200) {
        const result = response.data;
        
        // Update accountsMetadata and accounts in local state with the successful updates
        if (result.successful > 0) {
          const failedIds = new Set((result.failed || []).map(f => String(f.id)));
          const successfulUpdates = updates.filter(u => !failedIds.has(String(u.id)));
          if (successfulUpdates.length > 0) {
            const { activeProjectAttributes } = this.state;
            const updatesById = new Map(successfulUpdates.map(u => [String(u.id), u]));

            const updatedAccountsMetadata = (activeProjectAttributes.accountsMetadata || []).map(account => {
              const update = updatesById.get(String(account.id));
              return update ? { ...account, ...update } : account;
            });

            // Also sync the simplified accounts list used by dropdowns.
            // The simplified list's id field contains account_id values (not row PKs),
            // so use a separate map keyed by account_id for the lookup.
            const updatesByAccountId = new Map(successfulUpdates.map(u => [String(u.account_id), u]));
            const updatedAccounts = (activeProjectAttributes.accounts || []).map(account => {
              const update = updatesByAccountId.get(String(account.id));
              if (!update) return account;
              return {
                ...account,
                ...(update.account_name !== undefined && { label: update.account_name }),
                ...(update.entity_id !== undefined && { entityId: update.entity_id }),
                ...(update.department_id !== undefined && { departmentId: update.department_id }),
                ...(update.parent_account !== undefined && { parentAccount: update.parent_account }),
              };
            });

            this.setState(prevState => ({
              activeProjectAttributes: {
                ...prevState.activeProjectAttributes,
                accountsMetadata: updatedAccountsMetadata,
                accounts: updatedAccounts,
              },
            }));
          }
        }
        
        return result;
      }
      return null;
    } catch (error) {
      console.error('Error bulk updating GL Keys:', error);
      return null;
    }
  };

  /**
   * Bulk update monthly values for multiple accounts
   * @param {Array} updates - Array of { gl_key: string, dates: { "YYYY-MM-DD": number } }
   * @returns {Promise<Object|null>} Result with successful count and failed array, or null on error
   */
  _bulkUpdateMonthlyValues = async (updates) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await bulkUpdateMonthlyValues(
        accessToken,
        activeProjectInfo.id,
        updates
      );
      
      if (response.status === 200) {
        const result = response.data;
        
        // Update accountsMetadata in local state with the successful monthly value updates
        if (result.successful > 0) {
          const failedKeys = new Set((result.failed || []).map(f => f.gl_key));
          const successfulUpdates = updates.filter(u => !failedKeys.has(u.gl_key));
          if (successfulUpdates.length > 0) {
            const { activeProjectAttributes } = this.state;
            const updatesByGlKey = new Map(successfulUpdates.map(u => [u.gl_key, u]));
            const updatedAccountsMetadata = (activeProjectAttributes.accountsMetadata || []).map(account => {
              const update = updatesByGlKey.get(account.gl_key);
              return update ? { ...account, dates: { ...account.dates, ...update.dates } } : account;
            });
            this.setState(prevState => ({
              activeProjectAttributes: {
                ...prevState.activeProjectAttributes,
                accountsMetadata: updatedAccountsMetadata,
              },
            }));
          }
        }
        
        return result;
      }
      return null;
    } catch (error) {
      console.error('Error bulk updating monthly values:', error);
      return null;
    }
  };

  /**
   * Create a new GL account
   * @param {Object} accountData - Full account fields
   * @returns {Promise<Object|null>} Created account or null on error
   */
  _createAccount = async (accountData) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;

    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const payload = { ...accountData, project_id: activeProjectInfo.id };
      const response = await createAccount(payload, accessToken);
      if (response.status === 200 || response.status === 201) {
        const newAccount = response.data;
        // Add to accountsMetadata and accounts in state
        const formattedAccount = {
          departmentId: newAccount.department_id,
          entityId: newAccount.entity_id,
          id: newAccount.account_id,
          label: newAccount.account_name,
          parentAccount: newAccount.parent_account,
        };
        this.setState(prevState => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            accountsMetadata: [...(prevState.activeProjectAttributes.accountsMetadata || []), newAccount],
            accounts: [...(prevState.activeProjectAttributes.accounts || []), formattedAccount],
          },
        }));
        return newAccount;
      }
      return null;
    } catch (error) {
      console.error('Error creating account:', error);
      return null;
    }
  };

  /**
   * Create a new entity
   * @param {Object} entityData - { entity_id: string, entity_name: string }
   * @returns {Promise<Object|null>} Created entity { id, project_id, entity_id, entity_name } or null on error
   */
  _createEntity = async (entityData) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await createEntity(accessToken, activeProjectInfo.id, entityData);
      
      if (response.status === 201) {
        const newEntity = response.data;
        const updatedEntities = [...activeProjectAttributes.entities, newEntity];
        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            entities: updatedEntities,
          },
        }));
        return newEntity;
      }
      return null;
    } catch (error) {
      console.error('Error creating entity:', error);
      return null;
    }
  };

  /**
   * Rename an entity
   * @param {String} entityId - Entity ID to update
   * @param {Object} entityData - { entity_name: string }
   * @returns {Promise<Object|null>} Updated entity or null on error
   */
  _updateEntity = async (entityId, entityData) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await updateEntity(accessToken, activeProjectInfo.id, entityId, entityData);
      
      if (response.status === 200) {
        const updatedEntity = response.data;
        const newEntityName = updatedEntity.entity_name;
        const normalizedEntityId = String(entityId);

        const updatedEntities = activeProjectAttributes.entities.map(entity =>
          String(entity.entity_id) === normalizedEntityId
            ? { ...entity, entity_name: newEntityName }
            : entity
        );

        // Cascade new entity name into departments (entityName field)
        const updatedDepartments = (activeProjectAttributes.departments || []).map(dept =>
          String(dept.entityId) === normalizedEntityId
            ? { ...dept, entityName: newEntityName }
            : dept
        );

        // Cascade new entity name into accountsMetadata
        const updatedAccountsMetadata = (activeProjectAttributes.accountsMetadata || []).map(account =>
          String(account.entity_id) === normalizedEntityId
            ? { ...account, entity_name: newEntityName, entity: newEntityName }
            : account
        );

        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            entities: updatedEntities,
            departments: updatedDepartments,
            accountsMetadata: updatedAccountsMetadata,
          },
        }));
        return updatedEntity;
      }
      return null;
    } catch (error) {
      console.error('Error updating entity:', error);
      return null;
    }
  };

  /**
   * Delete an entity
   * @param {String} entityId - Entity ID to delete
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  _deleteEntity = async (entityId) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return false;
    }

    try {
      const response = await deleteEntity(
        accessToken,
        activeProjectInfo.id,
        entityId
      );
      
      if (response.status === 204 || response.status === 200) {
        // Remove entity from state
        const updatedEntities = activeProjectAttributes.entities.filter(
          entity => String(entity.entity_id) !== String(entityId)
        );
        
        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            entities: updatedEntities,
          },
        }));
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting entity:', error);
      return false;
    }
  };

  /**
   * Create a new department
   * @param {Object} departmentData - Department data { entity_id: string, department_id: string, department_name: string }
   * @returns {Promise<Object|null>} Created department or null on error
   */
  _createDepartment = async (departmentData) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await createDepartment(
        accessToken,
        activeProjectInfo.id,
        departmentData
      );
      
      if (response.status === 201) {
        const newDepartment = response.data;
        
        // Update departments in state
        const updatedDepartments = [...activeProjectAttributes.departments, {
          entityId: departmentData.entity_id,
          departmentId: departmentData.department_id,
          departmentName: departmentData.department_name,
        }];
        
        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            departments: updatedDepartments,
          },
        }));
        
        return newDepartment;
      }
      return null;
    } catch (error) {
      console.error('Error creating department:', error);
      return null;
    }
  };

  /**
   * Update an existing department
   * @param {String} entityId - Entity ID
   * @param {String} departmentId - Department ID to update
   * @param {Object} departmentData - Department data { department_name: string }
   * @returns {Promise<Object|null>} Updated department or null on error
   */
  _updateDepartment = async (entityId, departmentId, departmentData) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await updateDepartment(
        accessToken,
        activeProjectInfo.id,
        entityId,
        departmentId,
        departmentData
      );
      
      if (response.status === 200) {
        const updatedDepartment = response.data;
        const newDepartmentName = departmentData.department_name;
        const normalizedEntityId = String(entityId);
        const normalizedDepartmentId = String(departmentId);

        const updatedDepartments = activeProjectAttributes.departments.map(dept =>
          String(dept.entityId) === normalizedEntityId && String(dept.departmentId) === normalizedDepartmentId
            ? { ...dept, departmentName: newDepartmentName }
            : dept
        );

        // Cascade new department name into accountsMetadata
        const updatedAccountsMetadata = (activeProjectAttributes.accountsMetadata || []).map(account =>
          String(account.entity_id) === normalizedEntityId && String(account.department_id) === normalizedDepartmentId
            ? { ...account, department_name: newDepartmentName }
            : account
        );

        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            departments: updatedDepartments,
            accountsMetadata: updatedAccountsMetadata,
          },
        }));

        return updatedDepartment;
      }
      return null;
    } catch (error) {
      console.error('Error updating department:', error);
      return null;
    }
  };

  /**
   * Delete a department
   * @param {String} entityId - Entity ID
   * @param {String} departmentId - Department ID to delete
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  _deleteDepartment = async (entityId, departmentId) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return false;
    }

    try {
      const response = await deleteDepartment(
        accessToken,
        activeProjectInfo.id,
        entityId,
        departmentId
      );
      
      if (response.status === 204 || response.status === 200) {
        // Remove department from state
        const updatedDepartments = activeProjectAttributes.departments.filter(
          (dept) =>
            !(
              String(dept.entityId) === String(entityId) &&
              String(dept.departmentId) === String(departmentId)
            )
        );
        
        this.setState((prevState) => ({
          activeProjectAttributes: {
            ...prevState.activeProjectAttributes,
            departments: updatedDepartments,
          },
        }));
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting department:', error);
      return false;
    }
  };

  /**
   * Create a new parent account for the active project
   * @param {Object} data - { parent_account_name: string }
   * @returns {Promise<Object|null>} Created parent account { id, project_id, parent_account_name } or null on error
   */
  _createParentAccount = async (data) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;

    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await createParentAccount(accessToken, activeProjectInfo.id, data);
      if (response.status === 201) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error creating parent account:', error);
      return null;
    }
  };

  /**
   * Get parent accounts list for the active project
   * @returns {Promise<Array>} Array of parent account objects { id, project_id, parent_account_name }
   */
  _getParentAccounts = async () => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!accessToken || !activeProjectInfo || !activeProjectInfo.id) {
      return [];
    }

    try {
      const response = await getParentAccounts(accessToken, activeProjectInfo.id);
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching parent accounts:', error);
      return [];
    }
  };

  /**
   * Rename a parent account
   * @param {Number} parentAccountId - Integer PK of the parent account
   * @param {Object} data - { parent_account_name: string }
   * @returns {Promise<Object|null>} Updated parent account or null on error
   */
  _updateParentAccount = async (parentAccountId, data) => {
    const { activeProjectInfo, activeProjectAttributes } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return null;
    }

    try {
      const response = await updateParentAccount(accessToken, activeProjectInfo.id, parentAccountId, data);
      
      if (response.status === 200) {
        const updated = response.data;
        // Reload accounts metadata so accountsMetadata and the simplified accounts
        // list reflect the renamed parent_account string. The field is stored by name
        // (not ID) in both collections, so a full reload is the most reliable cascade.
        // Await to ensure the updated name is in state before the caller continues.
        await this._setActiveProjectAccountsAttribute(activeProjectInfo.id, accessToken);
        return updated;
      }
      return null;
    } catch (error) {
      console.error('Error updating parent account:', error);
      return null;
    }
  };

  /**
   * Delete a parent account
   * @param {Number} parentAccountId - Integer PK of the parent account
   * @returns {Promise<boolean>} True if successful
   */
  _deleteParentAccount = async (parentAccountId) => {
    const { activeProjectInfo } = this.state;
    const { activeUser } = this.context;
    const accessToken = activeUser?.accessToken;
    
    if (!activeProjectInfo || !activeProjectInfo.id) {
      console.error('No active project');
      return false;
    }

    try {
      const response = await deleteParentAccount(accessToken, activeProjectInfo.id, parentAccountId);
      return response.status === 204 || response.status === 200;
    } catch (error) {
      console.error('Error deleting parent account:', error);
      return false;
    }
  };

  /**
   * Load children for a parent account on-demand (lazy loading)
   * Used by AG Grid when user expands a parent account group
   * @param {Number} projectId - Project ID
   * @param {String} parentAccount - Parent account identifier
   * @param {String} accessToken - JWT access token
   * @returns {Promise<Array>} Array of child account data
   */
  _loadParentAccountChildren = async (projectId, parentAccount, accessToken) => {
    // Include projectId in cache/in-flight keys so same-named parent accounts across
    // different projects don't reuse each other's data.
    const cacheKey = `${projectId}:${parentAccount}`;

    // Check if already loaded in cache
    if (this.state.loadedParentChildren.has(cacheKey)) {
      return this.state.loadedParentChildren.get(cacheKey);
    }
    
    // If a request for this parent is already in-flight, return the shared Promise
    // so concurrent expansions await the same fetch instead of racing.
    if (this._inFlightParentLoads.has(cacheKey)) {
      return this._inFlightParentLoads.get(cacheKey);
    }
    
    // Mark as loading in state (for UI indicators); use cacheKey to avoid cross-project collisions
    if (this._isMounted) {
      this.setState(prevState => ({
        loadingParents: new Set(prevState.loadingParents).add(cacheKey)
      }));
    }
    
    const loadPromise = (async () => {
      try {
        const response = await getParentAccountChildren(
          projectId, 
          parentAccount, 
          accessToken
        );
        
        // dataApi helpers catch and return the error object instead of throwing,
        // so normalize status/data from either an axios response or an Error with a nested response.
        const isError = response instanceof Error || response?.isAxiosError;
        const status = response?.status ?? response?.response?.status;
        const responseData = response?.data ?? response?.response?.data;

        if (isError || status !== 200) {
          const errMsg = responseData?.errors
            ? (typeof responseData.errors === 'string' ? responseData.errors : JSON.stringify(responseData.errors))
            : (response?.message || `HTTP ${status ?? 'unknown'}`);
          throw new Error(`Failed to load children for "${parentAccount}": ${errMsg}`);
        }
        
        const children = responseData.children;
        
        // Cache the children and clear the loading flag; guard against unmounted component
        if (this._isMounted) {
          this.setState(prevState => {
            const newLoadedParentChildren = new Map(prevState.loadedParentChildren);
            newLoadedParentChildren.set(cacheKey, children);
            
            const newLoadingParents = new Set(prevState.loadingParents);
            newLoadingParents.delete(cacheKey);
            
            return {
              loadedParentChildren: newLoadedParentChildren,
              loadingParents: newLoadingParents
            };
          });
        }
        
        return children;
      } catch (err) {
        // Remove from loading set on error; guard against unmounted component
        if (this._isMounted) {
          this.setState(prevState => {
            const newLoadingParents = new Set(prevState.loadingParents);
            newLoadingParents.delete(cacheKey);
            return { loadingParents: newLoadingParents };
          });
        }
        throw err;
      } finally {
        // Always remove the in-flight entry so callers can retry on subsequent expansions
        this._inFlightParentLoads.delete(cacheKey);
      }
    })();
    
    // Store the shared promise before awaiting it
    this._inFlightParentLoads.set(cacheKey, loadPromise);
    
    return loadPromise;
  };

  /**
   * Clear the lazy-loaded parent children cache.
   * Call this when the grid refreshes so stale department/account rows are re-fetched.
   */
  _clearLoadedParentChildren = () => {
    this.setState({ loadedParentChildren: new Map() });
  };

  /**
   * Load department summaries for a parent account (hierarchical lazy loading - level 1)
   * @param {Number} projectId
   * @param {String} parentAccount
   * @param {String} accessToken
   * @returns {Promise<Array>} Array of department summary objects
   */
  _loadDepartmentsForParent = async (projectId, parentAccount, accessToken) => {
    const cacheKey = `${projectId}:${parentAccount}:departments`;

    if (this.state.loadedParentChildren.has(cacheKey)) {
      return this.state.loadedParentChildren.get(cacheKey);
    }

    if (this._inFlightParentLoads.has(cacheKey)) {
      return this._inFlightParentLoads.get(cacheKey);
    }

    if (this._isMounted) {
      this.setState(prevState => ({
        loadingParents: new Set(prevState.loadingParents).add(cacheKey)
      }));
    }

    const loadPromise = (async () => {
      try {
        const response = await getParentAccountDepartments(projectId, parentAccount, accessToken);

        const isError = response instanceof Error || response?.isAxiosError;
        const status = response?.status ?? response?.response?.status;
        const responseData = response?.data ?? response?.response?.data;

        if (isError || status !== 200) {
          const errMsg = responseData?.errors
            ? (typeof responseData.errors === 'string' ? responseData.errors : JSON.stringify(responseData.errors))
            : (response?.message || `HTTP ${status ?? 'unknown'}`);
          throw new Error(`Failed to load departments for "${parentAccount}": ${errMsg}`);
        }

        const departments = responseData.departments;

        if (this._isMounted) {
          this.setState(prevState => {
            const newCache = new Map(prevState.loadedParentChildren);
            newCache.set(cacheKey, departments);
            const newLoading = new Set(prevState.loadingParents);
            newLoading.delete(cacheKey);
            return { loadedParentChildren: newCache, loadingParents: newLoading };
          });
        }

        return departments;
      } catch (err) {
        if (this._isMounted) {
          this.setState(prevState => {
            const newLoading = new Set(prevState.loadingParents);
            newLoading.delete(cacheKey);
            return { loadingParents: newLoading };
          });
        }
        throw err;
      } finally {
        this._inFlightParentLoads.delete(cacheKey);
      }
    })();

    this._inFlightParentLoads.set(cacheKey, loadPromise);
    return loadPromise;
  };

  /**
   * Load accounts for a specific parent account + department (hierarchical lazy loading - level 2)
   * @param {Number} projectId
   * @param {String} parentAccount
   * @param {String} departmentId
   * @param {String} accessToken
   * @returns {Promise<Array>} Array of account objects
   */
  _loadAccountsForDepartment = async (projectId, parentAccount, departmentId, accessToken) => {
    const cacheKey = `${projectId}:${parentAccount}:${departmentId}:children`;

    if (this.state.loadedParentChildren.has(cacheKey)) {
      return this.state.loadedParentChildren.get(cacheKey);
    }

    if (this._inFlightParentLoads.has(cacheKey)) {
      return this._inFlightParentLoads.get(cacheKey);
    }

    if (this._isMounted) {
      this.setState(prevState => ({
        loadingParents: new Set(prevState.loadingParents).add(cacheKey)
      }));
    }

    const loadPromise = (async () => {
      try {
        const response = await getParentAccountDepartmentChildren(projectId, parentAccount, departmentId, accessToken);

        const isError = response instanceof Error || response?.isAxiosError;
        const status = response?.status ?? response?.response?.status;
        const responseData = response?.data ?? response?.response?.data;

        if (isError || status !== 200) {
          const errMsg = responseData?.errors
            ? (typeof responseData.errors === 'string' ? responseData.errors : JSON.stringify(responseData.errors))
            : (response?.message || `HTTP ${status ?? 'unknown'}`);
          throw new Error(`Failed to load accounts for "${parentAccount}" dept "${departmentId}": ${errMsg}`);
        }

        const children = responseData.children;

        if (this._isMounted) {
          this.setState(prevState => {
            const newCache = new Map(prevState.loadedParentChildren);
            newCache.set(cacheKey, children);
            const newLoading = new Set(prevState.loadingParents);
            newLoading.delete(cacheKey);
            return { loadedParentChildren: newCache, loadingParents: newLoading };
          });
        }

        return children;
      } catch (err) {
        if (this._isMounted) {
          this.setState(prevState => {
            const newLoading = new Set(prevState.loadingParents);
            newLoading.delete(cacheKey);
            return { loadingParents: newLoading };
          });
        }
        throw err;
      } finally {
        this._inFlightParentLoads.delete(cacheKey);
      }
    })();

    this._inFlightParentLoads.set(cacheKey, loadPromise);
    return loadPromise;
  };

  /**
   * Invalidate cached audit trail for a change or master stat
   * Call this after successful edits or comments to force refetch
   * @param {Number} id - Change ID or Master Stat ID
   * @param {String} type - 'change' or 'masterStat'
   */
  invalidateAuditTrailCache = (id, type = 'change') => {
    const cacheKey = `${type}_${id}`;
    delete this.auditTrailCacheRef[cacheKey];
  }

  render() {
    return (
      <DataContext.Provider value={{
        ...this.state,
        deleteProject: this._deleteProject,
        deleteAccountChange: this._deleteAccountChange,
        editAccountChange: this._editAccountChange,
        formatFileUploadData: this._formatFileUploadData,
        saveAccountChange: this._saveAccountChange,
        toggleAccountMenu: this._toggleAccountMenu,
        setActiveProject: this._setActiveProject,
        refreshActiveProject: this._refreshActiveProject,
        toggleDrawer: this._toggleDrawer,
        updateAgGridData: this._updateAgGridData,
        validateProjectData: this.validateProjectData,
        sanitizeProjectData: this.sanitizeProjectData,
        createMasterStat: this._createMasterStat,
        updateMasterStat: this._updateMasterStat,
        deleteMasterStat: this._deleteMasterStat,
        getMasterStats: this._getMasterStats,
        buildAgGridTable: this._buildAgGridTable,
        setActiveProjectEntitiesAttribute: this._setActiveProjectEntitiesAttribute,
        setActiveProjectDepartmentsAttribute: this._setActiveProjectDepartmentsAttribute,
        setActiveProjectAccountsAttribute: this._setActiveProjectAccountsAttribute,
        setActiveProjectChangesAttribute: this._setActiveProjectChangesAttribute,
        setActiveProjectDriversAttribute: this._setActiveProjectDriversAttribute,
        setActiveProjectCalculatedAccountsAttribute: this._setActiveProjectCalculatedAccountsAttribute,
        fetchAndCacheAuditTrail: this.fetchAndCacheAuditTrail,
        fetchAndCacheMasterStatAuditTrail: this.fetchAndCacheMasterStatAuditTrail,
        invalidateAuditTrailCache: this.invalidateAuditTrailCache,
        validateGLKeyUniqueness: this._validateGLKeyUniqueness,
        validateAccountNameInTaxonomy: this._validateAccountNameInTaxonomy,
        checkGLKeyDependencies: this._checkGLKeyDependencies,
        bulkDeleteGLKeys: this._bulkDeleteGLKeys,
        bulkUpdateGLKeys: this._bulkUpdateGLKeys,
        bulkUpdateMonthlyValues: this._bulkUpdateMonthlyValues,
        createAccount: this._createAccount,
        createEntity: this._createEntity,
        updateEntity: this._updateEntity,
        deleteEntity: this._deleteEntity,
        createDepartment: this._createDepartment,
        updateDepartment: this._updateDepartment,
        deleteDepartment: this._deleteDepartment,
        getParentAccounts: this._getParentAccounts,
        createParentAccount: this._createParentAccount,
        updateParentAccount: this._updateParentAccount,
        deleteParentAccount: this._deleteParentAccount,
        loadParentAccountChildren: this._loadParentAccountChildren,
        loadDepartmentsForParent: this._loadDepartmentsForParent,
        loadAccountsForDepartment: this._loadAccountsForDepartment,
        clearLoadedParentChildren: this._clearLoadedParentChildren,
      }}>
        {this.props.children}
      </DataContext.Provider>
    );
  }
}

// export const useDataContext = () => useContext(DataContext)

export default DataProvider;